// The sky: what the air sends to the observer's eye, along every direction, drawn in one full-screen pass behind everything.
//
// There is no dome. The pass is a screen-sized triangle that works out each pixel's view direction from the camera and looks the colour up in the sky-view table (skyView.ts): the radiance
// the atmosphere's tables give for the observer where it is (its height above the planet, the star's elevation at its eye), over the whole sphere of directions. The table is the same
// on the ground, ten kilometres up and in orbit; going up is the observer's height changing, and nothing in this file knows about a place, a tile or a ground. The cloud is not drawn
// here: the cloud volume (cloudVolume.ts) draws it, and sits in the sky this pass paints.
//
// What it takes from the server and what it works out itself:
//   * the atmosphere (atmospherePhysics.ts): everything about the colour and the level of the sky. The sky is flux x D, with D the table (per unit star irradiance outside the air) and
//     `flux` the star's irradiance there, through the same exposure and tone curve as ever (1 - exp(-6 gain L));
//   * the night floor from the sun message (sky_rgb): what the air does not give (starlight, airglow, a moon), added to the sky as a flat level where the sun is down;
//   * the weather message's aerosol scale (the painters' haze) and its cloud layers' covers, which the cloud lighting tables below still read.
// Without an atmosphere (before the server's message arrives) nothing is drawn.
//
// Fog and the background clear colour: `update` returns the colour distant land should fade to (the sky just above the true horizon, averaged round the compass), before the tone curve.
//
// For the cloud shaders: the uniforms they share (the light, the exposure, the tone curve, the haze toward the sky's colour, and the atmosphere's beam table they read each cloud's sunlight
// from: `cellUniforms`; atmosphereGpu.ts).
import * as THREE from 'three';
import { beamThroughClouds, type CloudLayerIn } from './skyPhysics';
import { makeTransTexture, SUN_TAP } from './atmosphereGpu';
import { SkyView, SV_NA, SV_NV } from './skyView';
import { SCOTOPIC, SCOTOPIC_ROD_W, SCOTOPIC_TINT_N, nightFactor } from './exposure';
import { ambientShUniforms } from './ambientSh';
import type { Atmosphere } from './atmospherePhysics';

/** The layer the sky pass is also on, for the probe's camera. */
export const SKY_PROBE_LAYER = 1;

const PROBE_SIZE = 128;          // cube face size in pixels
/** The eye may move this far (m) before the probe is drawn again whatever its period. */
const PROBE_MOVE_M = 150;

/** The server's "weather" message (see broadcast_server.py / weather.py). */
export interface WeatherMessage {
  type: 'weather';
  tick: number;
  day: number;
  seed: number;
  cloud_low: number;
  tau_low: number;
  cloud_high: number;
  tau_high: number;
  aerosol_scale: number;
  front: number;
  tau_total: [number, number, number];
  aerosol_depth: number;
  sun_rgb?: [number, number, number];
  sky_scatter?: number;
  horizon_sky?: number;
  twilight_dpd?: number;
  layers: { low: { height_m: number; drift_angle: number }; high: { height_m: number; drift_angle: number } };
}

export interface SkyFrame {
  /** Unit vector toward the star as the observer sees it (render axes: y up). */
  sunDir: THREE.Vector3;
  /** The star's elevation at the eye (radians). */
  elevation: number;
  /** The server's sky_rgb: the night floor is what it holds beyond the atmosphere's own sky. */
  skyRgb: ArrayLike<number>;
  /** The star's irradiance outside the air (the sun message's flux). */
  flux: number;
  adapt: number;                      // exposure gain
  baseExposure: number;               // tone-mapping exposure at gain 1 (renderer's BASE_EXPOSURE)
  skyExposure: number;                // SKY_EXPOSURE
  /** The observer's height above the planet (m). */
  eyeH: number;
  dt: number;                         // wall seconds since the last frame
  /** The moon, when the server sends one: unit direction in render axes, and its beam at the ground (the sun message's moon.rgb). */
  moon?: { dir: THREE.Vector3; rgb: ArrayLike<number> } | null;
}

/** Most the moon's beam may light a cloud above the clear sky's own level (luminance ratio). Real moonlit cloud is brighter still, but a display that
 *  holds the sky at mid grey would then show the cloud as a white blot. */
const MOON_CLOUD_CAP = 5;

const DEFAULT_WEATHER: WeatherMessage = {
  type: 'weather', tick: 0, day: 0, seed: 1, cloud_low: 0, tau_low: 4, cloud_high: 0, tau_high: 0.15, aerosol_scale: 1, front: 9,
  tau_total: [0.10, 0.15, 0.27], aerosol_depth: 0.05, sun_rgb: [1.0, 0.98, 0.92],
  layers: { low: { height_m: 1800, drift_angle: 0.6 }, high: { height_m: 8000, drift_angle: 1.1 } },
};

const EASE_S = 1.5;
const smoothstep = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const CLOUD_WB = 0.95;        // the most the sun's beam on cloud is whitened (by midday)
const CLOUD_KC = 3.0;

export const NOISE_GLSL = /* glsl */`
float hash13(vec3 p3) { p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float vnoise(vec3 p) {
  vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  float a = hash13(i), b = hash13(i + vec3(1,0,0)), c = hash13(i + vec3(0,1,0)), d = hash13(i + vec3(1,1,0));
  float e = hash13(i + vec3(0,0,1)), g = hash13(i + vec3(1,0,1)), h = hash13(i + vec3(0,1,1)), k = hash13(i + vec3(1,1,1));
  return mix(mix(mix(a, b, f.x), mix(c, d, f.x), f.y), mix(mix(e, g, f.x), mix(h, k, f.x), f.y), f.z);
}
// fbm of 'oct' octaves with octaves finer than a pixel faded to their mean (fw: footprint in cells of the first octave)
float fbm(vec3 p, float fw, int oct) {
  float sum = 0.0, wsum = 0.0, a = 0.5, f = 1.0;
  for (int i = 0; i < 6; i++) {
    if (i >= oct) break;
    float w = 1.0 - smoothstep(0.2, 0.65, fw * f);
    sum += a * (w * vnoise(p * f) + (1.0 - w) * 0.5);
    wsum += a;
    a *= 0.5; f *= 2.03; p += vec3(17.3, 5.1, 3.7);
  }
  return sum / wsum;
}
`;

/**
 * The sky-view table in a shader: the colour of the air along a view direction. The including shader declares `uSunDir` (unit, toward the star) and `PI` first. The table is D = pi x radiance
 * per unit star irradiance outside the air, stored over `uSVScale` (so half floats keep their precision in a dark sky); the sky is `uFlux` times it plus the night floor `uFloor`.
 * (The transmittance part is separate, SKY_TRANS_GLSL, for the far shell: `svTrans` is the view ray's transmittance to where it ends, the top of the air or the ground, by direction.)
 */
export const SKY_TRANS_GLSL = /* glsl */`
uniform sampler2D uSVT;      // the view ray's transmittance, one column, rows over elevation from the true horizon (finest there)
uniform float uElH;          // the elevation of the true horizon the table was laid out for (rad, <= 0)
float svRow(float el) {
  float up = el - uElH;
  float t = up >= 0.0 ? sqrt(up / max(1.57079632679 - uElH, 1e-5)) : -sqrt(-up / max(1.57079632679 + uElH, 1e-5));
  return 0.5 + 0.5 * t;
}
vec3 svTrans(vec3 d) { return texture2D(uSVT, vec2(0.5, svRow(asin(clamp(d.y, -1.0, 1.0))))).rgb; }
`;
export const SKY_VIEW_GLSL = /* glsl */`
${SKY_TRANS_GLSL}
uniform sampler2D uSV;       // D / uSVScale: rows as above, columns the azimuth from the star, 0..pi
uniform float uSVScale;
uniform float uFlux;         // the star's irradiance outside the air
uniform vec3  uFloor;        // the night floor (linear, the sky's units): what the ground's air holds, along a ray through all of it
uniform float uFloorRef;     // the blue scattering share (1 - transmittance) of the ground's zenith
vec3 svD(vec3 d) {
  vec2 sxz = normalize(uSunDir.xz + vec2(1e-6));
  vec2 vxz = normalize(d.xz + vec2(1e-6));
  float phi = acos(clamp(dot(sxz, vxz), -1.0, 1.0));
  return texture2D(uSV, vec2(phi / PI, svRow(asin(clamp(d.y, -1.0, 1.0))))).rgb * uSVScale;
}
// The floor is light the air sends (airglow, starlight and the moon's scattered light), so a ray has as much of it as it has air: its blue scattering share over the ground zenith's, and no more than
// the ground's. On the ground every direction has at least the zenith's air, so the floor is flat; from height the rays that go up out of the air have none and the ones along the limb have it all.
vec3 skyLinear(vec3 d) { return uFlux * svD(d) + uFloor * min(1.0, (1.0 - svTrans(d).b) / uFloorRef); }
`;

const VERT = /* glsl */`
uniform mat4 uInvProj;
varying vec3 vDir;
void main() {
  vec4 v = uInvProj * vec4(position.xy, 0.0, 1.0);
  vec3 vd = v.xyz / v.w;
  vDir = (vec4(vd, 0.0) * viewMatrix).xyz;                 // view space to render axes (the rotation's transpose)
  gl_Position = vec4(position.xy, 0.0, 1.0);
}`;

const FRAG = /* glsl */`
precision highp float;
varying vec3 vDir;
uniform vec3  uSunDir;
uniform float uGain;        // skyExposure * adapt
uniform float uNight;       // scotopic blend 0..1
uniform float uHpSky;
uniform float uDither;      // 1: dither the output here; 0 in the probe
uniform float uFrame;
uniform vec3  uUnder;       // underwater colour and how far the eye is under (0 = in air)
uniform float uUw;
const float PI = 3.14159265;
const vec3 ROD  = vec3(${SCOTOPIC_ROD_W.map(v => v.toFixed(5)).join(', ')});
const vec3 TINT = vec3(${SCOTOPIC_TINT_N.map(v => v.toFixed(5)).join(', ')});
const float SCOT = ${SCOTOPIC.STRENGTH.toFixed(3)};
${SKY_VIEW_GLSL}
${NOISE_GLSL}
vec3 scoto(vec3 c) { float rod = dot(c, ROD); return c + (rod * TINT - c) * (SCOT * uNight); }
vec3 tone(vec3 x, float hp) {
  vec3 a = 1.0 - exp(-x);
  float m = max(max(x.r, x.g), max(x.b, 1e-6));
  return mix(a, x / m * (1.0 - exp(-m)), hp);
}
void main() {
  vec3 d = normalize(vDir);
  vec3 col = tone(scoto(skyLinear(d)) * uGain, uHpSky);
  col = mix(col, uUnder, uUw);                  // eye under the sea: the water's colour replaces the sky
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
  // dither against banding, in display values after the encoding; not in the probe
  if (uDither > 0.5) gl_FragColor.rgb += (hash13(vec3(gl_FragCoord.xy, uFrame)) - 0.5) / 255.0;
}`;

function halfTexture(w: number, h: number): { tex: THREE.DataTexture; data: Uint16Array } {
  const data = new Uint16Array(w * h * 4);
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.HalfFloatType);
  tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping; tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false; tex.needsUpdate = true;
  return { tex, data };
}

/** Float values (3 per texel) into a half-float texture, divided by `scale`. */
function upload(src: Float32Array, n: number, dst: { tex: THREE.DataTexture; data: Uint16Array }, scale = 1) {
  const one = THREE.DataUtils.toHalfFloat(1), k = 1 / scale;
  for (let i = 0; i < n; i++) {
    dst.data[i * 4]     = THREE.DataUtils.toHalfFloat(Math.min(src[i * 3]! * k, 60000));
    dst.data[i * 4 + 1] = THREE.DataUtils.toHalfFloat(Math.min(src[i * 3 + 1]! * k, 60000));
    dst.data[i * 4 + 2] = THREE.DataUtils.toHalfFloat(Math.min(src[i * 3 + 2]! * k, 60000));
    dst.data[i * 4 + 3] = one;
  }
  dst.tex.needsUpdate = true;
}

const SKY_BANDS = 4;           // the sky-view solve runs in this many bands of rows, one per frame
const MIN_SOLVE_S = 0.1;        // fastest the tables are re-solved because the sun or the eye moved

/** The exposure's share to give back when the sky is a glow at one horizon (the sun just below it): gain / rms^TWILIGHT_GLOW_EXP, from the
 *  sun being 0 to 3 degrees below the horizon on, so day and dusk are untouched and deep night (rms back at 1) is too. */
export const TWILIGHT_GLOW_EXP = 0.3;
export function twilightGlowGain(glowRms: number, sunElevation: number): number {
  const dep = Math.max(0, -sunElevation) * 180 / Math.PI;
  const t = Math.min(1, dep / 3), w = t * t * (3 - 2 * t);
  return 1 / Math.pow(Math.max(1, glowRms), TWILIGHT_GLOW_EXP * w);
}

export class Sky {
  /** The sky pass: a screen-sized triangle, drawn first. */
  readonly mesh: THREE.Mesh;
  private mat: THREE.ShaderMaterial;
  /** The sky-view table (skyView.ts): what the air sends to the eye along every direction, for the observer where it is. */
  readonly view = new SkyView();
  private svT = halfTexture(SV_NA, SV_NV);
  private svTr = halfTexture(1, SV_NV);
  private svScale = 1;
  private atmT = makeTransTexture(null);
  private wx: WeatherMessage = DEFAULT_WEATHER;
  // eased values (cover, tau, haze)
  private cLow = 0; private tLow = 4; private cHigh = 0; private tHigh = 0.15; private aer = 1;
  private eased = false;
  private solved = { elev: 99, key: '', hs: -1 };
  private atmos: Atmosphere | null = null;
  private atmSerial = 0;
  private eyeAlt = 0;
  private sinceSolve = 1e9;      // seconds since the tables were last solved
  private frameNo = 0;
  /** The brightest the beam on a cloud gets (luminance, relative to the beam outside the air), over the heights of the two layers and over the distances out to the horizon: what the lit cloud's exposure is capped from. */
  private peakLit = 1;
  private medium = { aerosol: 1 };
  /** Linear colour (the sky's units, before the tone curve) distant land fades to: the sky a degree and a half above the true horizon, round the compass. */
  readonly horizonLinear = new THREE.Color();
  /** The sky as seen from the eye, in a small cube map: display colour in rgb (what the sea reflects and fogs into) and,
   *  in alpha, how much starlight the cloud in that direction lets through (what the stars and the Milky Way are
   *  scaled by, so they are hidden behind cloud and show in the gaps). Drawn by updateProbe. */
  readonly probe = new THREE.WebGLCubeRenderTarget(PROBE_SIZE, {
    type: THREE.HalfFloatType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
  /** True once the probe has been drawn. */
  probeReady = false;
  /** Counts the probe's redraws, so what is drawn from it (starProbe.ts) can follow. */
  probeSerial = 0;
  private probeCam = new THREE.CubeCamera(1, 1e7, this.probe);
  private probeAt = -1e9; private probeEye = new THREE.Vector3(); private probeDirty = false;
  /** Draw the probe at the next updateProbe whatever the period (the weather or the volume has changed at once). */
  invalidateProbe() { this.probeDirty = true; }
  /** The profiler's: a period that replaces the caller's (0 draws the probe every frame, to time a redraw); null leaves the caller's. */
  probePeriodOverride: number | null = null;
  // How much of the sun's beam the cloud in the sun's own direction lets through (0 behind thick cloud, 1 in clear sky), read from the
  // probe with a one-pixel draw and an asynchronous read-back (no stall; it lags a few frames, and is smoothed). Until the first read
  // arrives it is the coverage-wide figure.
  private sunPxRT = new THREE.WebGLRenderTarget(1, 1, { type: THREE.UnsignedByteType, depthBuffer: false });
  private sunPxMat = new THREE.ShaderMaterial({
    uniforms: { uCube: { value: null as THREE.Texture | null }, uDir: { value: new THREE.Vector3(0, 1, 0) } },
    vertexShader: 'void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader: `
      uniform samplerCube uCube; uniform vec3 uDir;
      void main() {
        vec3 d = normalize(uDir);
        vec3 a = normalize(cross(d, abs(d.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));
        vec3 b = cross(d, a);
        float c = textureLod(uCube, d, 0.0).a;
        float r = 0.0;
        for (int i = 0; i < 8; i++) {                     // a ring about 1.6 degrees out: the disc is small, the glare around it is not
          float ph = 0.7853982 * float(i);
          r += textureLod(uCube, normalize(d + 0.028 * (cos(ph) * a + sin(ph) * b)), 0.0).a;
        }
        gl_FragColor = vec4(mix(c, r * 0.125, 0.5), 0.0, 0.0, 1.0);
      }`,
    depthTest: false, depthWrite: false });
  private sunPxScene = new THREE.Scene();
  private sunPxCam = new THREE.Camera();
  private sunPxBusy = false; private sunPxFrame = 0; private sunPxGot = false; private sunPxDead = false;
  private sunPxBuf = new Uint8Array(4);
  private sunLocalT = 1;
  private volActive = false;
  private ready = false;

  constructor() {
    const u = (v: THREE.IUniform['value']) => ({ value: v });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uInvProj: u(new THREE.Matrix4()),
        uSV: u(this.svT.tex), uSVT: u(this.svTr.tex), uSVScale: u(1), uElH: u(0), uFlux: u(1), uFloor: u(new THREE.Vector3()), uFloorRef: u(1),
        uAtmT: u(this.atmT), uAtmG: u(new THREE.Vector4(6371000, 100000, 2, 2)), uEyeH: u(0), uEyeR: u(6371000),
        uSunDir: u(new THREE.Vector3(1, 0, 0)), uDither: u(1), uSrc: u(new THREE.Vector3(1, 1, 1)),
        ...ambientShUniforms(THREE.Vector3), uAmb: u(new THREE.Vector3(0.05, 0.06, 0.1)), uGain: u(6), uCgain: u(new THREE.Vector2(6, 6)), uNight: u(0),
        uHpSky: u(0.25), uHpCloud: u(0.85), uSunUp: u(0), uLDir: u(new THREE.Vector3(0, 1, 0)), uLUp: u(0), uMoonSrc: u(new THREE.Vector3()), uWB: u(0), uFrame: u(0), uSrgb: u(1), uProbe: u(0),
        uUnder: u(new THREE.Vector3()), uUw: u(0),
      },
      depthTest: false, depthWrite: false, fog: false, toneMapped: false,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1000;
    this.mesh.name = 'sky (full-screen pass)';
    this.mesh.onBeforeRender = (_r, _s, camera) => { (this.mat.uniforms.uInvProj!.value as THREE.Matrix4).copy(camera.projectionMatrixInverse); };
    this.mesh.layers.enable(SKY_PROBE_LAYER);      // drawn by the probe's camera as well as the main one
    this.probeCam.layers.set(SKY_PROBE_LAYER);     // ... which sees nothing else
    this.mesh.visible = false;          // until the first sky is solved: the renderer's own background shows meanwhile
  }

  /**
   * Draw the sky into the probe cube. Call once a frame after update() and before the main render; it redraws only when `periodS` seconds
   * have passed since the last draw (0: every call), or the eye has moved far (the probe is a picture from the eye), or `invalidateProbe()`
   * was called. The cloud moves slowly, and what reads the probe (the sea's reflection, the stars, the sun's disc) should not flicker
   * with the march's per-frame noise, so it is drawn about once a second.
   */
  updateProbe(renderer: THREE.WebGLRenderer, scene: THREE.Scene, eye: THREE.Vector3, periodS = 0) {
    if (!this.mesh.visible) return;                       // nothing solved yet
    const now = performance.now();
    periodS = this.probePeriodOverride ?? periodS;
    if (this.probeReady && !this.probeDirty && now - this.probeAt < periodS * 1000 && eye.distanceToSquared(this.probeEye) < PROBE_MOVE_M * PROBE_MOVE_M) return;
    this.probeAt = now; this.probeEye.copy(eye); this.probeDirty = false;
    const u = this.mat.uniforms;
    const srgb = u.uSrgb!.value;
    this.probeCam.position.copy(eye);
    this.probeCam.updateMatrixWorld(true);
    u.uProbe!.value = 1; u.uSrgb!.value = 0; u.uDither!.value = 0;       // plain display values: the probe is not an sRGB target
    const vp = this.volActive ? this.volumeProbe : null;
    vp?.beginProbe();
    try { this.probeCam.update(renderer, scene); } finally { vp?.endProbe(); }
    u.uProbe!.value = 0; u.uSrgb!.value = srgb; u.uDither!.value = 1;
    this.probeReady = true;
    this.probeSerial++;
  }

  /**
   * How much of the sun the cloud in front of it lets through, 0..1, for the sun disc and its glare: 1 in a clear patch, 0 behind
   * thick cloud (where nothing of the sun is visible but the lit cloud itself). Call once a frame after updateProbe, with the sun's
   * direction from the eye. Uses the probe's per-direction alpha (as the stars do) through a stricter curve, because a disc is
   * visible through far less cloud than a star: a veil that dims a star by half already hides the disc's edge.
   */
  sunCloudT(renderer: THREE.WebGLRenderer, dir: THREE.Vector3): number {
    if (!this.probeReady) return this.sunTransmit;
    const r = renderer as any;
    if (!this.sunPxBusy && !this.sunPxDead && this.sunPxFrame++ % 3 === 0 && typeof r.readRenderTargetPixelsAsync === 'function') {
      if (!this.sunPxScene.children.length) {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
        const m = new THREE.Mesh(g, this.sunPxMat); m.frustumCulled = false; this.sunPxScene.add(m);
      }
      this.sunPxMat.uniforms.uCube!.value = this.probe.texture;
      (this.sunPxMat.uniforms.uDir!.value as THREE.Vector3).copy(dir);
      const prev = renderer.getRenderTarget();
      renderer.setRenderTarget(this.sunPxRT);
      renderer.render(this.sunPxScene, this.sunPxCam);
      renderer.setRenderTarget(prev);
      this.sunPxBusy = true;
      r.readRenderTargetPixelsAsync(this.sunPxRT, 0, 0, 1, 1, this.sunPxBuf).then(() => {
        const a = this.sunPxBuf[0]! / 255;
        const t = Math.min(1, Math.max(0, (a - 0.3) / 0.6));
        const tgt = t * t * (3 - 2 * t);                    // smoothstep(0.3, 0.9, alpha)
        this.sunLocalT = this.sunPxGot ? this.sunLocalT + (tgt - this.sunLocalT) * 0.35 : tgt;
        this.sunPxGot = true; this.sunPxBusy = false;
      }, () => { this.sunPxBusy = false; this.sunPxDead = true; });
    }
    return this.sunPxGot ? this.sunLocalT : this.sunTransmit;
  }

  /** The uniforms the cloud shaders (cloudVolume.ts, cloudCells.ts) share with the sky, by reference: the sky-view table (the haze toward the sky's colour), the light, the exposure and the tone curve. */
  cellUniforms(): Record<string, THREE.IUniform> {
    const U = this.mat.uniforms, out: Record<string, THREE.IUniform> = {};
    for (const k of ['uSV', 'uSVT', 'uSVScale', 'uElH', 'uFlux', 'uFloor', 'uFloorRef', 'uAtmT', 'uAtmG', 'uEyeH', 'uEyeR', 'uSunDir', 'uSrc', 'uAmb', 'uSH', 'uShMix', 'uGain', 'uCgain', 'uNight', 'uHpSky', 'uHpCloud', 'uLDir', 'uLUp', 'uWB', 'uMoonSrc', 'uSrgb', 'uFrame']) out[k] = U[k]!;
    return out;
  }
  /** The ambient light's direction for the clouds (ambientSh.ts): `rel` = nine rgb triples (27 numbers) of spherical harmonics, each channel divided by the light on a face turned up; `mix` 0 gives the flat ambient back (`rel` may then be null). */
  setAmbientField(rel: ArrayLike<number> | null, mix: number) {
    const U = this.mat.uniforms, a = U.uSH!.value as THREE.Vector3[];
    if (rel) for (let k = 0; k < 9; k++) a[k]!.set(rel[3 * k]!, rel[3 * k + 1]!, rel[3 * k + 2]!);
    U.uShMix!.value = rel ? mix : 0;
  }
  /** Whether the cloud volume (cloudVolume.ts) is drawn into the probe too (so the sun's disc, the moon and the stars are dimmed by the cloud that is drawn). */
  setVolumeActive(on: boolean) { this.volActive = on && this.volumeProbe !== null; }
  /** The cloud volume as the probe sees it: `beginProbe` / `endProbe` bracket the probe's draw. */
  volumeProbe: { beginProbe(): void; endProbe(): void } | null = null;

  /** Tell the sky whether the renderer's output encoding is sRGB (the default). */
  setOutputSrgb(srgb: boolean) { this.mat.uniforms.uSrgb!.value = srgb ? 1 : 0; }

  /** With the eye under the sea the sky is replaced by the water's colour, by `amount` (0..1). */
  setUnderwater(color: THREE.Color, amount: number) { (this.mat.uniforms.uUnder!.value as THREE.Vector3).set(color.r, color.g, color.b); this.mat.uniforms.uUw!.value = amount; }

  /** A "weather" message from the server. */
  setWeather(msg: WeatherMessage) {
    this.wx = msg;
    if (!this.eased) { this.cLow = msg.cloud_low; this.tLow = msg.tau_low; this.cHigh = msg.cloud_high; this.tHigh = msg.tau_high; this.aer = msg.aerosol_scale; this.eased = true; }
  }

  /** What a viewer would see of the sun through the cloud, 0..1, by the weather's cover (the fallback until the probe is drawn), and how much of the
   *  night sky the cloud leaves visible (to dim stars and moon). */
  sunTransmit = 1;
  starVisibility = 1;
  /** How concentrated the sky's light is, as the root mean square of its luminance over the sky above the horizontal in units of the (cosine-weighted) mean the
   *  exposure is set from: about 1.2 to 3 by day, 1 in deep night, and up to 15 in the middle of nautical twilight, when nearly all of it is in a
   *  glow at the horizon on the sun's side. The renderer takes some of the exposure back by it (twilightGlowGain), as an eye turned toward the
   *  glow would. Follows the last sky solve. */
  glowRms = 1;
  /** The physical atmosphere the sky is solved from (atmospherePhysics.ts). The next frame solves everything again. */
  setAtmosphere(a: Atmosphere | null): void {
    this.atmos = a; this.atmSerial++;
    this.solved = { elev: 99, key: '', hs: -1 };
    this.job = null;
    const old = this.atmT; this.atmT = makeTransTexture(a);
    const U = this.mat.uniforms;
    U.uAtmT!.value = this.atmT; old.dispose();
    if (a) (U.uAtmG!.value as THREE.Vector4).set(a.Rb, a.H, a.tR, a.tMu);
    U.uFloorRef!.value = a ? Math.max(1e-3, 1 - a.trans3(0, 1, [0, 0, 0])[2]!) : 1;     // (the night floor's air: see skyLinear)
    if (!a) { this.ready = false; this.mesh.visible = false; }
  }
  get physical(): boolean { return this.atmos !== null; }
  /** The view-transmittance table and the horizon elevation it is laid out for (the far shell reads the air in front of the planet's surface from it). */
  get transTexture(): THREE.Texture { return this.svTr.tex; }
  get horizonElevation(): number { return this.view.elH; }
  /** Whether the sky is drawn: the table is a home-planet observer's, so away from the home planet it is off (set each frame by the renderer). */
  enabled = true;

  private job: { stage: number; elev: number } | null = null;

  /** Runs the next piece of the table solve in `job`: the sky-view table in four bands of rows (the bulk of the cost), then its upload. */
  private stepSolve(): void {
    const j = this.job, A = this.atmos; if (!j || !A) { this.job = null; return; }
    const st = j.stage;
    if (st < SKY_BANDS) {
      if (st === 0) this.view.begin(A, this.eyeAlt);
      this.view.fillRows(A, j.elev, this.medium, Math.round(st * SV_NV / SKY_BANDS), Math.round((st + 1) * SV_NV / SKY_BANDS));
    } else if (st === SKY_BANDS) {
      const V = this.view;
      V.finish();
      let peak = 0;
      for (let k = 0; k < V.rad.length; k++) if (V.rad[k]! > peak) peak = V.rad[k]!;
      this.svScale = peak > 1e-30 ? peak : 1;
      upload(V.rad, SV_NA * SV_NV, this.svT, this.svScale);
      upload(V.tr, SV_NV, this.svTr, 1);
      const U = this.mat.uniforms;
      U.uSVScale!.value = this.svScale; U.uElH!.value = V.elH;
      this.glowRms = V.glowRms;
      this.ready = true;
    }
    this.job = ++j.stage > SKY_BANDS ? null : j;
  }

  /** The brightest beam (luminance, relative to the beam outside the air) any cloud gets, over the given heights and over distances out toward the star (a cloud further along the curved
   *  planet sees the star higher, so at dusk the far ones are lit while the near ones are not): the lit cloud's exposure is capped from it. The sun's disc is taken as three taps, as in the shader. */
  private beamPeak(A: Atmosphere, el: number, heights: number[]): number {
    const b = [0, 0, 0];
    let peak = 0;
    for (const hc of heights) {
      for (const x of [0, 25e3, 50e3, 100e3, 200e3, 400e3]) {
        const z = Math.PI / 2 - (el + x / (A.Rb + hc));
        let l = 0;
        for (let k = -1; k <= 1; k++) { A.trans3(hc, Math.cos(z + k * SUN_TAP), b); l += (k === 0 ? 2 : 1) * (0.2126 * b[0]! + 0.7152 * b[1]! + 0.0722 * b[2]!); }
        peak = Math.max(peak, l / 4);
      }
    }
    return peak;
  }

  /** One line of what the clouds are lit with right now, for the profiler's report (so a colour complaint can be checked against the numbers). */
  describe(): string {
    const U = this.mat.uniforms, f3 = (v: THREE.Vector3) => `${v.x.toExponential(2)}, ${v.y.toExponential(2)}, ${v.z.toExponential(2)}`;
    const V = this.view;
    return `sky: ${this.ready ? `table for an eye ${(V.height / 1000).toFixed(2)} km up, star ${(V.starEl * 180 / Math.PI).toFixed(1)} deg, horizon at ${(V.elH * 180 / Math.PI).toFixed(1)} deg, scale ${this.svScale.toExponential(2)}, glow rms ${V.glowRms.toFixed(2)}` : 'not solved yet'}; `
      + `sky light: sun elevation ${(Math.asin(Math.min(1, U.uLUp!.value)) * 180 / Math.PI).toFixed(1)} deg (light on cloud), beam on cloud (${f3(U.uSrc!.value)}), ambient on cloud (${f3(U.uAmb!.value)}), white balance ${U.uWB!.value.toFixed(2)}, moon beam (${f3(U.uMoonSrc!.value)}), peak beam on cloud ${this.peakLit.toFixed(3)}, exposure gain ${U.uGain!.value.toExponential(2)}, cloud gain (${U.uCgain!.value.x.toFixed(3)}, ${U.uCgain!.value.y.toFixed(3)}), night ${U.uNight!.value.toFixed(2)}`;
  }

  update(f: SkyFrame): THREE.Color {
    const w = this.wx;
    const k = Math.min(1, f.dt / EASE_S);
    this.cLow += (w.cloud_low - this.cLow) * k; this.tLow += (w.tau_low - this.tLow) * k;
    this.cHigh += (w.cloud_high - this.cHigh) * k; this.tHigh += (w.tau_high - this.tHigh) * k;
    this.aer += (w.aerosol_scale - this.aer) * k;

    const A = this.atmos;
    if (!A || !this.enabled) { this.horizonLinear.setRGB(0, 0, 0); this.mesh.visible = false; return this.horizonLinear; }
    const sunRgb = A.sunToa;
    const eyeAlt = Math.max(0, f.eyeH);
    this.eyeAlt = eyeAlt;
    this.mat.uniforms.uEyeH!.value = eyeAlt; this.mat.uniforms.uEyeR!.value = A.Rb + eyeAlt;
    this.medium.aerosol = this.aer;
    const low: CloudLayerIn = { height: w.layers.low.height_m, cover: this.cLow, tau: this.tLow };
    const high: CloudLayerIn = { height: w.layers.high.height_m, cover: this.cHigh, tau: this.tHigh };

    // Tables: only when the sun, the air or the eye's height has moved enough to show. A solve is about 5 ms. The step in the sun's elevation is fine from high sun down to 11 degrees below
    // the horizon (where the sky changes fastest: the glare by day, the planet's shadow edge and the belt of Venus at dusk), easing back to 0.003 rad over 0.1 rad below that, and a sun that
    // moves fast is held to 10 solves a second. The solve is six pieces (the sky in four bands of rows, then the two layers' light) and runs one piece per frame, so no frame carries more
    // than a quarter of the sky's cost. The first solve, and one that follows a change of atmosphere, runs whole, so the sky is never drawn from a half-filled table.
    this.sinceSolve += f.dt;
    const e = f.elevation, tt = Math.min(1, Math.max(0, (-0.2 - e) / 0.1));
    const elevStep = 0.0003 + (0.003 - 0.0003) * tt * tt * (3 - 2 * tt);
    const key = 'a' + this.atmSerial + '|' + this.aer.toFixed(2) + '|' + low.height + '|' + high.height;
    const altS = Math.sqrt(eyeAlt);            // (a new solve when the eye has moved by about two metres at the ground, a few hundred metres at ten kilometres)
    if (!this.job && ((Math.abs(f.elevation - this.solved.elev) > elevStep && this.sinceSolve >= MIN_SOLVE_S) || key !== this.solved.key || (Math.abs(altS - this.solved.hs) > 1.0 && this.sinceSolve >= MIN_SOLVE_S))) {
      this.sinceSolve = 0;
      const whole = this.solved.key === '';
      this.solved = { elev: f.elevation, key, hs: altS };
      this.job = { stage: 0, elev: f.elevation };
      if (whole) while (this.job) this.stepSolve();
    }
    if (this.job) this.stepSolve();

    this.mesh.visible = this.ready;
    this.peakLit = this.beamPeak(A, f.elevation, [low.height, 4000, high.height, 12000]);
    const sinUp = Math.max(0, Math.sin(f.elevation));
    this.sunTransmit = f.elevation > 0 ? beamThroughClouds(sinUp, low, high) : 0;
    this.starVisibility = (1 - 0.9 * this.cLow * (1 - Math.exp(-this.tLow / 4))) * (1 - 0.35 * high.cover * (1 - Math.exp(-this.tHigh * 2)));

    const night = nightFactor(f.baseExposure * f.adapt);
    const U = this.mat.uniforms;
    (U.uSunDir!.value as THREE.Vector3).copy(f.sunDir);
    // The clear sky is the table. What it lacks at night (starlight, airglow, a moon) is the server's sky_rgb: where the sun is down, whatever of it the atmosphere's own sky does not
    // already hold is added as a flat level. By day the table alone (the gaps between clouds are this blue).
    const es = A.esky3(eyeAlt, Math.sin(f.elevation));
    const sc = [f.flux * es[0]!, f.flux * es[1]!, f.flux * es[2]!];
    const clearMix = smoothstep(-0.07, 0.0, f.elevation);
    const fl = [0, 1, 2].map(i => (1 - clearMix) * Math.max(0, f.skyRgb[i]! - sc[i]!));
    U.uFlux!.value = f.flux;
    (U.uFloor!.value as THREE.Vector3).set(fl[0]!, fl[1]!, fl[2]!);
    (U.uSrc!.value as THREE.Vector3).set(f.flux * sunRgb[0], f.flux * sunRgb[1], f.flux * sunRgb[2]);
    // ambient on cloud: the sky's own light, colour and level, as it is at the eye (the diffuse light on an open horizontal surface). Nothing is done to its hue.
    // The ambient field (ambientSh.ts) is that same light divided out per channel, so what reaches a face is the light it sees: the up face gets this colour exactly, a face
    // turned to the ground or a bank of cloud gets what is there. (A hue taken out of this colour here, and the field still divided by the full one, does not cancel: the
    // faces that do not look up come out the colour of the ratio, which is orange by day and grows with the eye's height as the sky's blue thins.)
    (U.uAmb!.value as THREE.Vector3).set(f.skyRgb[0]!, f.skyRgb[1]!, f.skyRgb[2]!);
    U.uGain!.value = f.skyExposure * f.adapt;
    U.uNight!.value = night;
    // The sunlit part of a cloud is far brighter than the sky at dusk, and the global exposure (set by the dark ground) would
    // clip it flat. A viewer's eye does not: it holds the brightest lit cloud near the top of the range and lets the rest fall
    // away from it, so the exposure for the lit part is capped to keep the brightest value near KC, until the glow itself fades.
    const srcL = f.flux * (0.2126 * sunRgb[0] + 0.7152 * sunRgb[1] + 0.0722 * sunRgb[2]);
    const g0 = f.skyExposure * f.adapt;
    const gc = g0 * Math.min(1, CLOUD_KC / Math.max(1e-9, g0 * this.peakLit * srcL));
    (U.uCgain!.value as THREE.Vector2).set(gc, gc);
    U.uSunUp!.value = sinUp;
    U.uWB!.value = CLOUD_WB * smoothstep(0.02, 0.22, sinUp);

    // Light on the clouds. By day it is the sun's. By night the moon's beam lights them (they are the brightest thing in a moonlit
    // sky, and their shadowed side is dark), from the moon's direction; the one that gives the cloud more light sets the direction.
    // With no moon up the sun's direction is kept and the moon term is 0, so nothing changes.
    const mUp = f.moon ? Math.min(1, Math.max(0, (f.moon.dir.y + 0.02) / 0.12)) : 0;       // a moon below the horizon lights no cloud
    const mr = f.moon && mUp > 0 ? [f.moon.rgb[0]! * mUp, f.moon.rgb[1]! * mUp, f.moon.rgb[2]! * mUp] : null;
    const mLum = mr ? Math.max(0, 0.2126 * mr[0]! + 0.7152 * mr[1]! + 0.0722 * mr[2]!) : 0;
    const sunCloudLum = srcL * this.peakLit;
    const wm = mLum > 0 && f.moon ? mLum / (mLum + sunCloudLum + 1e-30) : 0;
    const LD = U.uLDir!.value as THREE.Vector3;
    if (wm > 0 && f.moon && mr) {
      const skyLum = 0.2126 * f.skyRgb[0]! + 0.7152 * f.skyRgb[1]! + 0.0722 * f.skyRgb[2]!;
      const kk = Math.min(1, MOON_CLOUD_CAP * skyLum / Math.max(1e-30, 0.6 * mLum));
      (U.uMoonSrc!.value as THREE.Vector3).set(mr[0]! * kk, mr[1]! * kk, mr[2]! * kk);
      LD.copy(f.sunDir).lerp(f.moon.dir, wm).normalize();
    } else {
      (U.uMoonSrc!.value as THREE.Vector3).set(0, 0, 0);
      LD.copy(f.sunDir);
    }
    U.uLUp!.value = Math.max(0, LD.y);
    U.uFrame!.value = (this.frameNo++ % 61);

    // fog / clear colour: the sky just above the true horizon, round the compass (before the tone curve, in the sky's units)
    const hz = this.view.horizon;
    this.horizonLinear.setRGB(f.flux * hz[0] + fl[0]!, f.flux * hz[1] + fl[1]!, f.flux * hz[2] + fl[2]!);
    return this.horizonLinear;
  }

  dispose() {
    this.mesh.geometry.dispose(); this.mat.dispose();
    this.sunPxRT.dispose(); this.sunPxMat.dispose(); this.probe.dispose();
    this.svT.tex.dispose(); this.svTr.tex.dispose(); this.atmT.dispose();
  }
}
